import { DragEvent, useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import {
  FieldLabel,
  QueryAdderFieldGridItem,
  QueryAdderInheritedParentServiceSection,
  QueryAdderLayoutSkeleton,
  QueryAdderModalShell,
  QueryAdderScopedStyles,
  QueryAdderStickyHeader,
  QueryAdderTopicLinkingCard,
  SectionCard,
  queryAdderWideTwoColumnGridSx,
  queryAdderTwoColumnGridSx,
  renderQueryAdderCell,
} from './QueryAdderLayoutShared';
import VerificationModal from '../VerificationModal/VerificationModal';
import { TrackerDataType } from '../../shared/constants/constants';
import {
  getQueryAdderUiPalette,
  queryAdderPalette,
  queryAdderTypeScale,
} from '../../shared/constants/queryAdderPalette';
import { LOG_ADDER_CONFIG, TOPIC_LINKING_COPY } from './logAdderConfig';

const ui = getQueryAdderUiPalette(TrackerDataType.MEDIA_INTERACTION);
const mediaAdderConfig = LOG_ADDER_CONFIG.mediaInteraction;

const infoText = {
  topic: 'Use a clear Topic name so related media work can be found, grouped, and reported consistently.',
  linkedService: 'Link this Media Interaction to a broader Service when it belongs to a larger request or project.',
  type: 'Choose the type of media work so similar Interactions can be grouped and reported consistently.',
  units: 'Select the units connected to this Interaction.',
  entryDate: 'Use Entry Date to mark when the task or work begins.',
  dueDate: 'Use Due Date for the target timeline, or as the completion date once the work is finished.',
  dept: 'Add the department when it helps narrow the context or ownership.',
  expert: 'Use Lead/Expert for the spokesperson, subject-matter expert, or requester tied to this Interaction.',
  campaign: 'Use Campaign to group this Interaction under a broader initiative when relevant.',
  outlet: 'Choose the media outlet connected to this Interaction.',
  journalist: 'Select the journalist involved in this Interaction.',
  lead: 'Use Team Members for the staff doing the work so ownership stays visible.',
  contactMethod: 'Choose how this Interaction happened.',
  tone: 'Capture the tone of the Interaction when that context is useful.',
  status: 'Set the current stage of this Interaction.',
  response: 'Capture the response details so the Interaction history stays useful later on.',
  keyMessaging: 'Store the key Messaging used in this Interaction.',
  file: 'Add the Story Link for the published story or coverage tied to this Interaction.',
};

const fieldLabels = {
  topic: 'Topic',
  linkedService: 'Linked Service',
  entryDate: 'Entry Date',
  dueDate: 'Due Date',
  type: 'Type(s) of Work',
  units: 'Unit',
  dept: 'Department',
  expert: 'Lead/Expert',
  campaign: 'Campaign',
  outlet: 'Media Outlet',
  journalist: 'Journalist',
  lead: 'Team Members',
  contactMethod: 'Mode of Communication',
  tone: 'Tone',
  status: 'Status',
  response: 'Background/Response',
  keyMessaging: 'Key Messaging',
  file: 'Comms Material',
};

const mediaMetadataGridSx = {
  ...queryAdderWideTwoColumnGridSx,
};

type QueryAdderFields = Record<string, any>;
type DropdownTable = Record<string, any>;
type QueryAdderCallback = (value: any, field: string) => void;
type QueryAdderDragHandler = (event: DragEvent<any>) => void;

interface MediaInteractionQueryAdderLayoutProps {
  title: string;
  fields: QueryAdderFields;
  dropdownTable: DropdownTable;
  requiredInputs: string[];
  errorCols: string[];
  eventHandler: Record<string, any>;
  files?: any[];
  handleDrag: QueryAdderDragHandler;
  handleDrop: QueryAdderDragHandler;
  isDragActive?: boolean;
  isUpdatingRow?: boolean;
  currMonth?: string | number | Date;
  callback: QueryAdderCallback;
  onSubmit: () => void;
  onCancel: () => void;
  isUpdatingItem?: boolean;
  onDelete?: () => void;
  loading?: boolean;
}

interface RenderMediaFieldArgs
  extends Pick<
    MediaInteractionQueryAdderLayoutProps,
    | 'fields'
    | 'dropdownTable'
    | 'callback'
    | 'currMonth'
    | 'requiredInputs'
    | 'errorCols'
    | 'eventHandler'
    | 'files'
    | 'handleDrag'
    | 'handleDrop'
    | 'isDragActive'
    | 'isUpdatingRow'
  > {
  col: string;
  fullWidth?: boolean;
  helperText?: string;
}

function renderMediaField({
  col,
  fields,
  dropdownTable,
  callback,
  currMonth,
  requiredInputs,
  errorCols,
  eventHandler,
  files,
  handleDrag,
  handleDrop,
  isDragActive,
  isUpdatingRow,
  fullWidth = false,
  helperText,
}: RenderMediaFieldArgs) {
  const fieldLabelMap = fieldLabels as Record<string, string>;
  const infoTextMap = infoText as Record<string, string>;
  const cell = renderQueryAdderCell({
    col,
    fields,
    dropdownTable,
    callback,
    currMonth,
    requiredInputs,
    errorCols,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdatingRow,
    hideLabel: true,
    labelText: fieldLabelMap[col] || col,
    fullWidthContainer: true,
  });

  return (
    <QueryAdderFieldGridItem key={col} fullWidth={fullWidth}>
      <FieldLabel
        label={fieldLabelMap[col] || col}
        required={requiredInputs.includes(col)}
        info={infoTextMap[col]}
        ui={ui}
      />
      {cell}
      {helperText ? (
        <Typography
          sx={{ mt: 1, pl: 1.25, fontSize: queryAdderTypeScale.helperText, lineHeight: 1.55, color: ui.helper }}
        >
          {helperText}
        </Typography>
      ) : null}
    </QueryAdderFieldGridItem>
  );
}

function MediaInteractionQueryAdderLayout(props: Readonly<MediaInteractionQueryAdderLayoutProps>) {
  const {
    fields,
    dropdownTable,
    requiredInputs,
    errorCols,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdatingRow,
    currMonth,
    callback,
    onSubmit,
    onCancel,
    isUpdatingItem,
    onDelete,
    loading,
  } = props;
  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const [isLinkedToService, setIsLinkedToService] = useState(
    Array.isArray(fields.linkedService) && fields.linkedService.length > 0
  );
  const matchParentTopic = true;
  const [isTopicLinkingExpanded, setIsTopicLinkingExpanded] = useState(true);
  const [focusLinkedService, setFocusLinkedService] = useState(false);

  useEffect(() => {
    if (Array.isArray(fields.linkedService) && fields.linkedService.length > 0 && !isLinkedToService) {
      setIsLinkedToService(true);
    }
  }, [fields.linkedService, isLinkedToService]);

  const selectedService = useMemo<any>(() => {
    const linkedService = Array.isArray(fields.linkedService) ? fields.linkedService[0] : null;
    if (!linkedService) return null;
    const linkedServiceId = linkedService._id || linkedService.id || linkedService;
    return (dropdownTable?.service || []).find((service: any) => service._id === linkedServiceId) || linkedService;
  }, [dropdownTable, fields.linkedService]);

  const selectedServiceId = selectedService?._id || selectedService?.id || '';
  const selectedServiceTitle = selectedService?.name || selectedService?.service || '';
  const serviceLinkUi = {
    ...ui,
    service: ui.service || queryAdderPalette.service.main,
    serviceDark: ui.serviceDark || queryAdderPalette.service.accentDark,
    serviceTile: ui.serviceTile || queryAdderPalette.service.tile,
  };

  useEffect(() => {
    if (isLinkedToService && matchParentTopic && selectedServiceTitle && fields.topic !== selectedServiceTitle) {
      callback(selectedServiceTitle, 'topic');
    }
  }, [callback, fields.topic, isLinkedToService, matchParentTopic, selectedServiceTitle]);

  const handleLinkedServiceToggle = (nextChecked: boolean) => {
    setIsLinkedToService(nextChecked);
    if (nextChecked && !selectedServiceTitle) {
      setFocusLinkedService(true);
      setIsTopicLinkingExpanded(true);
    }
    if (!nextChecked) {
      callback([], 'linkedService');
      setIsTopicLinkingExpanded(true);
    }
  };

  useEffect(() => {
    if (!focusLinkedService) return;
    const timer = setTimeout(() => setFocusLinkedService(false), 300);
    return () => clearTimeout(timer);
  }, [focusLinkedService]);

  const topicLinkingSearchField = (
    <Box sx={{ width: '100%' }}>
      {renderQueryAdderCell({
        col: 'linkedService',
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
        hideLabel: true,
        labelText: 'Search Services',
        placeholderText: 'Search Services',
        autoFocusLinkedService: focusLinkedService,
        fullWidthContainer: true,
      })}
    </Box>
  );

  const inheritedFields = selectedService ? (
    <QueryAdderInheritedParentServiceSection
      ui={serviceLinkUi}
      infoText="When you add a Media Interaction under a linked Service, shared details inherit from the parent Service so related work stays connected."
    >
      {renderMediaField({
        col: 'units',
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
      })}
      {renderMediaField({
        col: 'dept',
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
      })}
      {renderMediaField({
        col: 'expert',
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
      })}
      {renderMediaField({
        col: 'campaign',
        fields,
        dropdownTable,
        callback,
        currMonth,
        requiredInputs,
        errorCols,
        eventHandler,
        files,
        handleDrag,
        handleDrop,
        isDragActive,
        isUpdatingRow,
      })}
    </QueryAdderInheritedParentServiceSection>
  ) : null;

  return (
    <Box className="query-adder-media-layout" sx={{ height: '100%', backgroundColor: ui.white }}>
      <QueryAdderScopedStyles
        scopeClassName=".query-adder-media-layout"
        ui={ui}
        options={{ preserveFieldChrome: true, preserveRichTextChrome: true }}
      />
      <QueryAdderModalShell
        header={
          <QueryAdderStickyHeader
            title={mediaAdderConfig.modalTitle}
            infoText="Capture Interactions and keep outlet, journalist, response details, and related materials in one shared workflow."
            icon={mediaAdderConfig.icon}
            ui={ui}
            onClose={onCancel}
            onSubmit={onSubmit}
            onCancel={onCancel}
            onDelete={onDelete ? () => setOpenVerificationModal(true) : undefined}
            isUpdatingItem={isUpdatingItem || loading}
            isUpdatingRow={isUpdatingRow}
            topBorderColor={mediaAdderConfig.logColor}
            titleSx={{ color: ui.title }}
            iconCardSx={{
              backgroundColor: ui.tile,
              color: ui.main,
              borderRadius: '12px',
            }}
          />
        }
        bodySx={{ backgroundColor: ui.white }}
        contentSx={{ pb: 4, backgroundColor: ui.white }}
      >
        {loading ? (
          <QueryAdderLayoutSkeleton ui={ui} />
        ) : (
          <Stack spacing={3}>
            <QueryAdderTopicLinkingCard
              ui={serviceLinkUi}
              eyebrow={TOPIC_LINKING_COPY.sectionTitle}
              checkboxLabel={TOPIC_LINKING_COPY.panelTitle}
              checkboxHelperText={TOPIC_LINKING_COPY.helperText}
              checked={isLinkedToService}
              onCheckedChange={handleLinkedServiceToggle}
              selectedServiceTitle={selectedServiceTitle}
              selectedServiceId={selectedServiceId}
              expanded={isTopicLinkingExpanded}
              onExpandedChange={setIsTopicLinkingExpanded}
              searchField={topicLinkingSearchField}
              inheritedFields={inheritedFields}
              inheritedInfoText="Use this when this record belongs under an existing Service."
            />

            <SectionCard title="Details" icon={<ForumOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
              <Box sx={mediaMetadataGridSx}>
                {[
                  ...(isLinkedToService ? [] : ['topic']),
                  'entryDate',
                  'dueDate',
                  'type',
                  ...(isLinkedToService ? [] : ['units', 'dept', 'expert', 'campaign']),
                  'lead',
                  'journalist',
                  'outlet',
                  'contactMethod',
                  'tone',
                  'status',
                ].map((col) =>
                  renderMediaField({
                    col,
                    fields,
                    dropdownTable,
                    callback,
                    currMonth,
                    requiredInputs,
                    errorCols,
                    eventHandler,
                    files,
                    handleDrag,
                    handleDrop,
                    isDragActive,
                    isUpdatingRow,
                    fullWidth: !isLinkedToService && col === 'topic',
                  })
                )}
              </Box>
            </SectionCard>

            <SectionCard title="Notes and Files" icon={<FolderOpenOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                <Box sx={{ mb: 4 }}>
                  {renderMediaField({
                    col: 'response',
                    fields,
                    dropdownTable,
                    callback,
                    currMonth,
                    requiredInputs,
                    errorCols,
                    eventHandler,
                    files,
                    handleDrag,
                    handleDrop,
                    isDragActive,
                    isUpdatingRow,
                    fullWidth: true,
                  })}
                </Box>

                {renderMediaField({
                  col: 'keyMessaging',
                  fields,
                  dropdownTable,
                  callback,
                  currMonth,
                  requiredInputs,
                  errorCols,
                  eventHandler,
                  files,
                  handleDrag,
                  handleDrop,
                  isDragActive,
                  isUpdatingRow,
                  fullWidth: true,
                })}
              </Box>

              <Box sx={{ ...queryAdderTwoColumnGridSx, mt: 4 }}>
                <Box sx={{ gridColumn: { xs: 'span 1', lg: 'span 2' } }}>
                  <FieldLabel
                    label="Story Link"
                    info="Add the published story URL with a clear title and short description once coverage goes live."
                    ui={ui}
                  />
                  <Box
                    sx={{
                      mt: 1.25,
                      border: `1px solid ${ui.divider}`,
                      borderRadius: '14px',
                      backgroundColor: ui.white,
                      px: 2.25,
                      py: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {renderQueryAdderCell({
                        col: 'file',
                        fields,
                        dropdownTable,
                        callback,
                        currMonth,
                        requiredInputs,
                        errorCols,
                        eventHandler,
                        files,
                        handleDrag,
                        handleDrop,
                        isDragActive,
                        isUpdatingRow,
                        hideLabel: true,
                        fullWidthContainer: true,
                        fileRenderMode: 'uploadOnly',
                      })}
                    </Box>
                    <Typography
                      sx={{
                        mt: 0.4,
                        fontSize: queryAdderTypeScale.helperText,
                        lineHeight: 1.5,
                        color: ui.helper,
                        textAlign: 'center',
                      }}
                    >
                      Add the published story link so this interaction stays connected to the final coverage.
                    </Typography>
                  </Box>
                  {files && files.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      {renderQueryAdderCell({
                        col: 'file',
                        fields,
                        dropdownTable,
                        callback,
                        currMonth,
                        requiredInputs,
                        errorCols,
                        eventHandler,
                        files,
                        handleDrag,
                        handleDrop,
                        isDragActive,
                        isUpdatingRow,
                        hideLabel: true,
                        fullWidthContainer: true,
                        fileRenderMode: 'listOnly',
                      })}
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </SectionCard>
          </Stack>
        )}
      </QueryAdderModalShell>
      {onDelete ? (
        <VerificationModal
          openVerificationModal={openVerificationModal}
          setOpenVerificationModal={setOpenVerificationModal}
          modalTitle="Confirm Deletion"
          modalDescription="Are you sure you want to delete this Media Interaction?"
          accentColor={ui.main}
          handleYesClick={() => {
            onDelete();
            setOpenVerificationModal(false);
          }}
        />
      ) : null}
    </Box>
  );
}

export default MediaInteractionQueryAdderLayout;
